import type { ReactNode } from 'react';
import {
  BookingLedgerRow,
  DeductionLedgerRow,
  VariationLedgerRow,
  canCancelWork,
  percentOf,
  type LedgerCollapse,
  type LedgerColumn,
  type SitePlot,
} from '../../../shared/components/ledger';
import { DEMO_WORK_TYPE, type BookedRow } from './data';
import { WorkHoverCard } from './WorkHoverCard';

// Pot breakdown and activity share a flat row shape, so they share this
// mapping. A real screen's adapter does the same job against its own DTO.

export interface BookedRowContext {
  columns: LedgerColumn[];
  collapse: LedgerCollapse;
  onOpen: (label: string) => void;
  /** Activity shows where the work happened; the pot bands already say it. */
  withSitePlot?: boolean;
}

function sitePlotOf(
  row: BookedRow,
  ctx: BookedRowContext
): SitePlot | undefined {
  if (!ctx.withSitePlot || !row.site || !row.plot) {
    return undefined;
  }
  return { site: row.site, plot: row.plot };
}

function hoverCard(row: BookedRow, kind: string, target?: number): ReactNode {
  return (
    <WorkHoverCard
      title={row.title}
      kind={kind}
      reference={row.ref}
      status={row.status}
      who={row.bookedBy}
      whenIso={row.bookedAt}
      amount={row.amount}
      target={target}
      sitePlot={row.site && row.plot ? `${row.site} · ${row.plot}` : undefined}
      approvedBy={row.approvedBy}
      approvedAt={row.approvedAt}
      rejectedBy={row.rejectedBy}
      rejectionReason={row.rejectionReason}
    />
  );
}

export function renderBookedRow(
  row: BookedRow,
  ctx: BookedRowContext
): ReactNode {
  const shared = {
    columns: ctx.columns,
    collapse: ctx.collapse,
    status: row.status,
    reference: row.ref,
    amount: row.amount,
    onClick: () => ctx.onOpen(row.ref),
  };

  if (row.kind === 'deduction') {
    return (
      <DeductionLedgerRow
        key={row.ref}
        {...shared}
        title={row.title}
        createdBy={row.bookedBy}
        createdAt={row.bookedAt}
        hoverCard={hoverCard(row, 'Deduction')}
      />
    );
  }

  const menu = {
    label: 'Cancel booking',
    disabled: !canCancelWork(row.status),
    onSelect: () => ctx.onOpen(`Cancel ${row.ref}`),
  };

  if (row.kind === 'variation') {
    return (
      <VariationLedgerRow
        key={row.ref}
        {...shared}
        workTypeName={DEMO_WORK_TYPE}
        description={row.title}
        bookedBy={row.bookedBy}
        bookedAt={row.bookedAt}
        sitePlot={sitePlotOf(row, ctx)}
        menu={menu}
        hoverCard={hoverCard(row, DEMO_WORK_TYPE)}
      />
    );
  }

  return (
    <BookingLedgerRow
      key={row.ref}
      {...shared}
      title={row.title}
      bookedBy={row.bookedBy}
      bookedAt={row.bookedAt}
      sitePlot={sitePlotOf(row, ctx)}
      percentBooked={
        row.fullAmount != null && row.fullAmount > row.amount
          ? percentOf(row.amount, row.fullAmount)
          : undefined
      }
      menu={menu}
      hoverCard={hoverCard(row, 'Labour item', row.fullAmount)}
    />
  );
}
